const API_BASE = '/api';

export class ApiError extends Error {
  status: number;
  retryAfter?: number | undefined;
  constructor(message: string, status: number, retryAfter?: number) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
    this.name = 'ApiError';
  }
}

export type Role = 'admin' | 'developer_operator' | 'manufacturer_operator';

export interface SessionUser {
  id: string;
  username: string;
  role: Role;
  active?: boolean;
  /** Present in /auth/me and login DTO since #80. */
  emailVerified?: boolean;
  /** Present when the backend exposes it; optional on older DTOs. */
  email?: string | null;
  /** Temp-password fallback (#78): the SPA gates navigation until changed. */
  mustChangePassword?: boolean;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length)) || null;
    }
  }
  return null;
}

let csrfToken: string | null = null;

export function getCsrfToken(): string | null {
  if (!csrfToken) {
    csrfToken = readCookie('ls_csrf');
  }
  return csrfToken;
}

export function clearCsrfToken(): void {
  csrfToken = null;
}

/** SPA bootstrap: refresh the CSRF token for an existing session. */
export async function bootstrapCsrf(): Promise<void> {
  try {
    const resp = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' });
    if (resp.ok) {
      const data = (await resp.json()) as { csrfToken?: string };
      if (data.csrfToken) csrfToken = data.csrfToken;
    }
  } catch {
    // best-effort — next mutating request will surface any real problem
  }
}

interface ApiFetchOptions extends RequestInit {
  /** Skip the automatic redirect to /login on 401 (used by auth bootstrap). */
  skipAuthRedirect?: boolean;
}

export async function apiFetch(endpoint: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { skipAuthRedirect, ...init } = options;
  const method = (init.method ?? 'GET').toUpperCase();

  // Operator data must never come from the browser's HTTP cache: a conditional GET
  // answered with 304 has an empty body and a non-2xx status, which reads as a failure
  // even though the cached body was fine. no-store keeps every API call unconditional.
  init.cache = 'no-store';
  const headers = new Headers(init.headers || {});
  headers.set('Content-Type', 'application/json');

  if (MUTATING_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) {
      headers.set('X-CSRF-Token', token);
    }
  }

  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}${endpoint}`, { ...init, headers, credentials: 'include' });
  } catch {
    throw new ApiError('Could not reach the server. Try again.', 0);
  }

  if (resp.status === 401) {
    if (!skipAuthRedirect && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401);
  }

  if (resp.status === 429) {
    const raw = resp.headers.get('Retry-After');
    const retryAfter = raw ? parseInt(raw, 10) : NaN;
    throw new ApiError(
      'Too Many Requests',
      429,
      Number.isFinite(retryAfter) ? retryAfter : undefined
    );
  }

  if (resp.status === 403) {
    // RBAC denial — surface a stable marker, UI translates it (errors.accessDenied).
    throw new ApiError('Forbidden', 403);
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => 'Unknown error');
    throw new ApiError(text, resp.status);
  }

  return resp;
}

/**
 * Public forgot-password (#79). Backend always answers 200 with a generic
 * message (enumeration-safe) — callers must not vary UI by response.
 * Throws ApiError 429 on rate limit, ApiError 0 on network failure.
 */
export async function forgotPasswordRequest(email: string): Promise<void> {
  await apiFetch('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipAuthRedirect: true,
  });
}

/**
 * Public reset-password (#79). 200 {ok:true} on success; invalid/expired/used
 * token → generic 400 (no oracle); rate limit → 429.
 */
export async function resetPasswordRequest(token: string, password: string): Promise<void> {
  await apiFetch('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
    skipAuthRedirect: true,
  });
}

export async function loginRequest(login: string, password: string): Promise<SessionUser> {
  const resp = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ login, password }),
    skipAuthRedirect: true,
  });
  // Backend returns the public user flat: { id, username, role }.
  const user = (await resp.json()) as SessionUser;
  // The login response also sets the ls_csrf cookie — pick it up for this tab.
  csrfToken = readCookie('ls_csrf');
  return user;
}

export async function fetchMe(): Promise<SessionUser | null> {
  try {
    const resp = await apiFetch('/auth/me', { skipAuthRedirect: true });
    return (await resp.json()) as SessionUser;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function logoutRequest(): Promise<void> {
  await apiFetch('/auth/logout', { method: 'POST', skipAuthRedirect: true });
}

/** Public: consume a one-time email verification token (#80). Generic 400 on invalid/expired. */
export async function verifyEmailRequest(token: string): Promise<void> {
  await apiFetch('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
    skipAuthRedirect: true,
  });
}

/** Public: request a new verification email (#80). Generic 200; 429 when rate-limited. */
export async function resendVerificationRequest(email: string): Promise<void> {
  await apiFetch('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipAuthRedirect: true,
  });
}

export interface SetPasswordResult {
  ok: boolean;
  id: string;
  username: string;
  emailVerified?: boolean;
}

/** Public invite landing: one-time token + new password → activate operator. No CSRF/session needed. */
export async function setPasswordRequest(
  token: string,
  password: string
): Promise<SetPasswordResult> {
  const resp = await apiFetch('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
    skipAuthRedirect: true,
  });
  return (await resp.json()) as SetPasswordResult;
}

export interface ChangePasswordResult {
  ok: boolean;
  mustChangePassword: boolean;
  emailVerified?: boolean;
}

/** Authenticated password change (force-change after temp password, or voluntary). */
export async function changePasswordRequest(
  currentPassword: string,
  newPassword: string
): Promise<ChangePasswordResult> {
  const resp = await apiFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
    skipAuthRedirect: true,
  });
  return (await resp.json()) as ChangePasswordResult;
}
